import React, {
  useEffect,
  useId,
  useRef,
  useState,
  useMemo,
  useCallback,
  createContext,
  useContext,
} from 'react'
import clsx from 'clsx'
import Select, {
  components,
  type MultiValue,
  type SingleValue,
  type MenuListProps,
  type OptionProps,
} from 'react-select'
import './ApplyV2Select.scss'

interface Option {
  value: string | number
  label?: string | null
}

interface BaseProps {
  isRequired?: boolean
  label?: string
  className?: string
  id?: string | number
  name?: string
  isDisabled?: boolean
  isLoading?: boolean
  errorMessage?: string | null | boolean | React.ReactNode
  isShowErrorMessage?: boolean
  onFocus?: React.FocusEventHandler<HTMLDivElement>
  onBlur?: React.FocusEventHandler<HTMLDivElement>
  options?: Option[]
  placeholder?: string | number
  isSearchable?: boolean
  isClearable?: boolean
  description?: React.ReactNode
  inputValue?: string | number | null
  onInputChange?: (input: string) => void
  prefocusOptionValue?: string | number
  onMenuOpen?: () => void
  onMenuClose?: () => void
}

interface SingleSelectProps extends BaseProps {
  isMulti?: false
  value?: string | number | boolean | null
  onChange?: (value: string | number | null) => void
}

interface MultiSelectProps extends BaseProps {
  isMulti: true
  value?: (string | number)[]
  onChange?: (value: (string | number)[]) => void
}

type ApplyV2SelectProps = SingleSelectProps | MultiSelectProps

const baseClass = 'ApplyV2Select'

// ---------- Prefocus контекст (залишаємо як було)
const PrefocusCtx = createContext<{ targetValue?: string | number } | null>(null)

const PrefocusMenuList = (props: MenuListProps<any, boolean>) => {
  const ctx = useContext(PrefocusCtx)
  const listRef = useRef<HTMLElement | null>(null)
  const hasRunRef = useRef(false)

  const setListRef = (node: HTMLElement | null) => {
    listRef.current = node
    hasRunRef.current = false
  }

  useEffect(() => {
    if (hasRunRef.current) return
    if (!listRef.current) return
    if (ctx?.targetValue == null) return
    const listEl = listRef.current

    const t = setTimeout(() => {
      const target = listEl.querySelector<HTMLElement>('[data-prefocus="true"]')
      if (target) {
        target.scrollIntoView({ block: 'center' })
        hasRunRef.current = true
        return
      }
      const desired = String(ctx.targetValue)
      const byAria = Array.from(listEl.querySelectorAll<HTMLElement>('[role="option"]')).find((el) =>
        (el.getAttribute('aria-label') || '').includes(desired)
      )
      if (byAria) {
        byAria.scrollIntoView({ block: 'center' })
        hasRunRef.current = true
        return
      }
      const byText = Array.from(listEl.querySelectorAll<HTMLElement>('[role="option"]')).find(
        (el) => (el.textContent || '').trim() === desired
      )
      if (byText) byText.scrollIntoView({ block: 'center' })
      hasRunRef.current = true
    }, 30)

    return () => clearTimeout(t)
  }, [ctx?.targetValue])

  return <components.MenuList {...props} innerRef={setListRef} />
}

const PrefocusOption = (props: OptionProps<any, boolean>) => {
  const ctx = useContext(PrefocusCtx)
  const isPrefocus = ctx?.targetValue !== undefined && props.data?.value === ctx.targetValue
  return (
    <div data-prefocus={isPrefocus ? 'true' : undefined}>
      <components.Option {...props} />
    </div>
  )
}

// ---------- Свій Control з надійним шевроном (absolute)
const Control = (props: any) => {
  const { menuIsOpen } = props.selectProps
  return (
    <components.Control {...props}>
      {props.children}
      <span
        className="ApplyV2Select__input__chevron"
        aria-hidden="true"
        data-open={menuIsOpen ? 'true' : 'false'}
      />
    </components.Control>
  )
}

function ApplyV2Select({
  value,
  isRequired = false,
  label,
  className,
  id,
  name,
  isDisabled = false,
  isLoading = false,
  errorMessage,
  isShowErrorMessage = true,
  onChange = () => {},
  onFocus = () => {},
  onBlur = () => {},
  options = [],
  placeholder = '',
  isClearable = false,
  isSearchable = false,
  isMulti = false,
  description,
  inputValue,
  onInputChange = () => {},
  prefocusOptionValue,
  onMenuOpen,
  onMenuClose,
}: ApplyV2SelectProps) {
  const selectRef = useRef<any>(null)
  const defaultId = useId()
  const selectId = id ? id.toString() : defaultId

  const [currentValue, setCurrentValue] = useState(value)

  const optionsWithLabels = useMemo(
    () =>
      options.map((o) => ({
        value: o.value,
        label: o.label ?? o.value.toString(),
      })),
    [options]
  )

  const selectValue = useMemo(() => {
    if (isMulti) {
      return optionsWithLabels.filter((o) => (currentValue as (string | number)[])?.includes(o.value))
    }
    return optionsWithLabels.find((o) => o.value === currentValue)
  }, [isMulti, optionsWithLabels, currentValue])

  const handleChange = (selected: MultiValue<Option> | SingleValue<Option>) => {
    if (isMulti) {
      const values = (selected as MultiValue<Option>).map((opt) => opt.value)
      ;(onChange as (value: (string | number)[]) => void)(values)
    } else {
      const v = (selected as SingleValue<Option>)?.value ?? null
      ;(onChange as (value: string | number | null) => void)(v)
    }
  }

  useEffect(() => {
    setCurrentValue(value as any)
  }, [value])

  // Збираємо компоненти разом (зберігаємо Prefocus + свій Control)
  const mergedComponents = useMemo(() => {
    const base: any = { Control, DropdownIndicator: () => null, IndicatorSeparator: () => null }
    if (prefocusOptionValue !== undefined && prefocusOptionValue !== null) {
      base.MenuList = PrefocusMenuList
      base.Option = PrefocusOption
    }
    return base
  }, [prefocusOptionValue])

  const handleInputChange = useCallback(
    (input: string) => {
      onInputChange(input)
      return input
    },
    [onInputChange]
  )

  return (
    <div
      className={clsx(baseClass, className, {
        [`${baseClass}--${name}`]: name,
        [`${baseClass}--disabled`]: isDisabled,
        [`${baseClass}--loading`]: isLoading,
        [`${baseClass}--error`]: errorMessage,
        [`${className}--disabled`]: className && isDisabled,
        [`${className}--loading`]: className && isLoading,
        [`${className}--error`]: className && errorMessage,
      })}
    >
      {label && (
        <label className={`${baseClass}__label`} htmlFor={name ?? selectId}>
          {label}
          {isRequired && '*'}
        </label>
      )}

      <PrefocusCtx.Provider value={{ targetValue: prefocusOptionValue }}>
        <Select
          ref={selectRef}
          inputId={name ?? selectId}
          id={name ?? selectId}
          name={name}
          value={selectValue as any}
          inputValue={inputValue != null ? inputValue.toString() : undefined}
          options={optionsWithLabels}
          placeholder={placeholder || ''}
          isClearable={isClearable}
          onChange={handleChange}
          onInputChange={(val) => handleInputChange(val)}
          onFocus={onFocus}
          onBlur={onBlur}
          onMenuOpen={onMenuOpen}
          onMenuClose={onMenuClose}
          isDisabled={isDisabled}
          isLoading={isLoading}
          isSearchable={isSearchable}
          isMulti={isMulti}
          className={clsx(`${baseClass}__input`, {
            [`${className}__input`]: className,
          })}
          classNamePrefix={`${baseClass}__input`}
          menuPlacement="auto"
          menuPosition="fixed"
          components={mergedComponents}
        />
      </PrefocusCtx.Provider>

      {description && (
        <div
          className={clsx(`${baseClass}__description`, {
            [`${className}__description`]: className,
          })}
        >
          {description}
        </div>
      )}

      {errorMessage && isShowErrorMessage && (
        <div
          className={clsx(`${baseClass}__error`, {
            [`${className}__error`]: className,
          })}
        >
          <div
            className={clsx(`${baseClass}__error--icon`, {
              [`${className}__error--icon`]: className,
            })}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8 1.933C4.65 1.933 1.933 4.65 1.933 8c0 3.351 2.717 6.067 6.067 6.067S14.067 11.351 14.067 8 11.35 1.933 8 1.933ZM7.4 4.667a.6.6 0 1 1 1.2 0v4a.6.6 0 1 1-1.2 0v-4ZM8 11.333a.667.667 0 1 0 0-1.333.667.667 0 0 0 0 1.333Z"
                fill="#B62626"
              />
            </svg>
          </div>
          <div
            className={clsx(`${baseClass}__error--text`, {
              [`${className}__error--text`]: className,
            })}
          >
            {errorMessage}
          </div>
        </div>
      )}
    </div>
  )
}

export default ApplyV2Select
